import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { BriefcaseIcon, TrophyIcon, GraduationCapIcon, LinkedinIcon } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
const founders = [{
  name: 'Trung Nguyen',
  role: 'Partner',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/wMkEhubvxoFoZTXc72Qxp2/Trung_Nguyen.jpg',
  education: 'BSc Finance & Data Science @ Northeastern',
  linkedin: 'https://www.linkedin.com/in/realtrungnguyen',
  experiences: [{
    type: 'leadership',
    title: 'Founder',
    company: 'Aidworks Foundation (550+ families served, $13.5k+ annually)'
  }, {
    type: 'leadership',
    title: 'Co-Founder',
    company: 'Northeastern Investment Banking Group (Interim President | M&A Division)'
  }, {
    type: 'work',
    title: 'FX Sales & Trading Summer Analyst',
    company: 'Orient Commercial Bank (OCB) - Institutional Sales'
  }, {
    type: 'work',
    title: 'Investment Summer Analyst',
    company: 'Vietnam Ministry of Finance (HCMC Finance & Investment SOE)'
  }, {
    type: 'work',
    title: 'Hedge Fund Equity Research Intern',
    company: 'VinaCapital ($4B AUM Mega-fund)'
  }, {
    type: 'work',
    title: 'Private Equity Extern',
    company: 'Project Destined (Retail Real Estate Group)'
  }, {
    type: 'work',
    title: 'Spring Private Equity Analyst',
    company: 'CPG Family Office (Multi-Family & Mixed Use Real Estate)'
  }, {
    type: 'work',
    title: 'Fall Equity Research Extern',
    company: 'Westlake Securities (Middle-Market Investment Bank)'
  }, {
    type: 'work',
    title: 'Corporate VC & Business Analytics Extern',
    company: 'HP (Series A & B, AI startups)'
  }, {
    type: 'achievement',
    title: 'Various Insight Programs',
    company: 'Goldman Sachs'
  }]
}, {
  name: 'Alejandro Vides',
  role: 'Partner',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/czY7e6EnsToqT7uUZMR3Vg/Ale_picture.jpg',
  education: 'BSc Math & Philosophy (Honors) @ Northeastern',
  linkedin: 'https://www.linkedin.com/in/a-vides/',
  experiences: [{
    type: 'leadership',
    title: 'Co-Founder',
    company: 'The Daily Benji (Fintech - Financial awareness via email)'
  }, {
    type: 'work',
    title: 'Summer Analyst',
    company: 'EKANTA Wealth Advisors'
  }, {
    type: 'leadership',
    title: 'Co-Founder',
    company: 'The Life Studio (Won US Congressional App Challenge)'
  }, {
    type: 'achievement',
    title: 'Crypto & NFT Trader',
    company: '$60k profit from $300 seed (200x return, $1.9M+ volume)'
  }, {
    type: 'work',
    title: 'Youth Sailing Coach',
    company: 'Coconut Grove Sailing Club Foundation'
  }, {
    type: 'achievement',
    title: 'Reseller',
    company: '$25k+ revenue across multiple product categories'
  }]
}, {
  name: 'Tivin Wu Lin',
  role: 'Partner',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/dGV8hmNmpe7WhUmPS6ySin/Tivin_picture.jpg',
  education: 'BSBA Finance, Minor in Mathematics @ Northeastern',
  linkedin: 'https://www.linkedin.com/in/tivinwulin/',
  experiences: [{
    type: 'leadership',
    title: 'Mentorship Content & Engagement Coordinator',
    company: 'International Students in Business (ISIB)'
  }, {
    type: 'leadership',
    title: 'Teacher Assistant',
    company: 'Northeastern (6+ hrs/week, graded 300+ case studies)'
  }, {
    type: 'leadership',
    title: 'Finance Chair',
    company: 'Northeastern ($5000+ funding requests, 4-member board)'
  }, {
    type: 'work',
    title: 'Legislative Intern',
    company: 'Costa Rica Government (Drafted 15+ weekly briefs)'
  }, {
    type: 'work',
    title: 'Senior Manager',
    company: 'Mercado Mayorista AV6 (35+ staff, $5M+ inventory)'
  }, {
    type: 'work',
    title: 'Accounting Assistant',
    company: 'GBC ASESORES ECONOMICOS ($100M+ transactions processed)'
  }, {
    type: 'leadership',
    title: 'Hispanic Honors Society President',
    company: 'Maranatha High School (Raised $2500+, 40+ members)'
  }, {
    type: 'leadership',
    title: 'Math Club President',
    company: 'Maranatha High School (400+ Pi-day attendees)'
  }]
}];
const getExperienceIcon = (type: string) => {
  switch (type) {
    case 'work':
      return BriefcaseIcon;
    case 'achievement':
      return TrophyIcon;
    case 'leadership':
      return GraduationCapIcon;
    default:
      return BriefcaseIcon;
  }
};
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 40,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
export function WhoWeAre() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [selectedFounder, setSelectedFounder] = useState<number | null>(null);
  return <section ref={ref} className="w-full py-32 px-6 bg-navy-900" data-id="element-383">
      <div className="max-w-6xl mx-auto" data-id="element-384">
        <motion.h2 initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="text-4xl md:text-5xl font-bold mb-12 text-center text-white" data-id="element-385">
          Who We Are
        </motion.h2>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: [0.22, 1, 0.36, 1]
      }} className="prose prose-lg max-w-4xl mx-auto mb-16" data-id="element-386">
          <p className="text-lg md:text-xl text-slate leading-relaxed mb-6" data-id="element-387">
            Pericles Partners is an independent, student-driven quantitative
            research lab. We explore the intersection of finance with
            mathematics, data science, computer science, economics, and the
            humanities.
          </p>
          <p className="text-lg md:text-xl text-slate leading-relaxed" data-id="element-388">
            We combine rigorous modeling with unconventional thinking because
            the best ideas often appear where disciplines collide.
          </p>
        </motion.div>
        <motion.div variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="grid md:grid-cols-3 gap-8" data-id="element-389">
          {founders.map((founder, index) => {
          const isExpanded = selectedFounder === index;
          return <motion.div key={founder.name} variants={itemVariants} data-id="element-390">
                <motion.div className="bg-navy-800 border-2 border-navy-700 rounded-lg overflow-hidden hover:border-steel transition-all duration-300 cursor-pointer shadow-lg" onClick={() => setSelectedFounder(isExpanded ? null : index)} whileHover={{
              y: -12,
              boxShadow: '0 20px 40px rgba(30, 58, 138, 0.3)'
            }} transition={{
              duration: 0.3
            }} data-id="element-391">
                  <div className="relative h-64 overflow-hidden" data-id="element-392">
                    <motion.img src={founder.image} alt={founder.name} className="w-full h-full object-cover" whileHover={{
                  scale: 1.05
                }} transition={{
                  duration: 0.4
                }} data-id="element-393" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-transparent to-transparent" data-id="element-394" />
                  </div>
                  <div className="p-6 bg-navy-800" data-id="element-395">
                    <div className="flex items-start justify-between mb-1" data-id="element-396">
                      <h3 className="text-2xl font-bold text-white" data-id="element-397">
                        {founder.name}
                      </h3>
                      <motion.a href={founder.linkedin} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-steel hover:text-white transition-colors" whileHover={{
                    scale: 1.2,
                    rotate: 360
                  }} transition={{
                    duration: 0.4
                  }} data-id="element-398">
                        <LinkedinIcon className="w-6 h-6" data-id="element-399" />
                      </motion.a>
                    </div>
                    <p className="text-sm text-slate mb-3 font-semibold" data-id="element-400">
                      {founder.role}
                    </p>
                    <p className="text-sm text-slate mb-4" data-id="element-401">
                      {founder.education}
                    </p>
                    <button className="text-sm text-steel hover:text-white transition-colors flex items-center gap-2 font-semibold" onClick={e => {
                  e.stopPropagation();
                  setSelectedFounder(isExpanded ? null : index);
                }} data-id="element-402">
                      {isExpanded ? 'Hide' : 'View'} Experience
                      <motion.span animate={{
                    rotate: isExpanded ? 180 : 0
                  }} transition={{
                    duration: 0.3
                  }} data-id="element-403">
                        ▼
                      </motion.span>
                    </button>
                  </div>
                </motion.div>
                <AnimatePresence data-id="element-404">
                  {isExpanded && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.4,
                ease: [0.22, 1, 0.36, 1]
              }} className="mt-4 bg-navy-800 border-2 border-navy-700 rounded-lg overflow-hidden shadow-lg" data-id="element-405">
                      <div className="p-6 space-y-4 max-h-96 overflow-y-auto" data-id="element-406">
                        {founder.experiences.map((exp, expIndex) => {
                    const Icon = getExperienceIcon(exp.type);
                    return <motion.div key={expIndex} initial={{
                      opacity: 0,
                      x: -20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.3,
                      delay: expIndex * 0.05
                    }} whileHover={{
                      x: 8,
                      backgroundColor: 'rgba(226, 232, 240, 0.05)'
                    }} className="flex items-start gap-3 p-3 bg-navy-900/50 rounded-lg transition-colors" data-id="element-407">
                              <div className="mt-1" data-id="element-408">
                                <Icon className="w-5 h-5 text-steel" data-id="element-409" />
                              </div>
                              <div data-id="element-410">
                                <p className="text-sm font-semibold text-white" data-id="element-411">
                                  {exp.title}
                                </p>
                                <p className="text-xs text-slate" data-id="element-412">
                                  {exp.company}
                                </p>
                              </div>
                            </motion.div>;
                  })}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}